<template>
  <div>
    <v-tooltip right color="primary">
      <template v-slot:activator="{ on: tooltip }">
        <v-dialog
          v-model="dialog"
          persistent
          max-width="650px"
          @input="initLocation()"
        >
          <template v-slot:activator="{ on: dialog }">
            <v-btn
              class="ml-2 mr-0"
              small
              icon
              v-on="{ ...tooltip, ...dialog }"
            >
              <v-icon v-if="edit" color="info">edit</v-icon>
              <v-icon v-else color="success">add_circle</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-form
              ref="form"
              v-model="valid"
              lazy-validation
              autocomplete="off"
              @submit.prevent="
                loading = true;
                edit ? valid && editLocation() : valid && createLocation();
              "
            >
              <v-app-bar flat dark color="primary">
                <v-toolbar-title v-if="edit">Edit Location</v-toolbar-title>
                <v-toolbar-title v-else>Add Location</v-toolbar-title>
              </v-app-bar>
              <v-card-text class="pt-3">
                <v-container grid-list-lg class="pt-0">
                  <v-text-field
                    v-model="location.name"
                    label="Name*"
                    :rules="[v => !!v || 'Name is required']"
                    required
                  />
                  <address-lookup
                    v-if="dialog"
                    v-model="location.address"
                    label="Address"
                  />
                  <v-textarea
                    v-model="location.description"
                    label="Description"
                    maxlength="145"
                  />
                  <v-select
                    v-model="location.features"
                    :items="featuresList"
                    item-text="name"
                    item-value="name"
                    label="Features"
                    multiple
                    dense
                    :menu-props="{ 'offset-y': true }"
                  >
                    <v-chip
                      slot="selection"
                      slot-scope="data"
                      close
                      small
                      @input="data.parent.selectItem(data.item)"
                    >
                      <v-avatar
                        color="white"
                        style="transform: translateX(-1px);"
                      >
                        <img
                          :src="require('@/assets/features/' + data.item.image)"
                          :alt="data.item.name"
                        />
                      </v-avatar>
                      <span>{{ data.item.name }}</span>
                    </v-chip>
                  </v-select>
                  <v-flex xs12 md6>
                    <upload-image
                      v-if="dialog"
                      path="locations"
                      :edit-url="edit ? location.image_download_url : null"
                      :min-dimensions="[1125, 812]"
                      :max-file-size="5000000"
                      @downloadpath="setDownloadUrl($event)"
                    />
                  </v-flex>
                  <small>*indicates required field</small>
                </v-container>
              </v-card-text>
              <v-card-actions class="pt-0">
                <v-spacer />
                <v-btn color="error" text @click="cancel()">Cancel</v-btn>
                <v-btn
                  v-if="edit"
                  color="success"
                  :disabled="!valid"
                  type="submit"
                  :loading="loading"
                  >Update</v-btn
                >
                <v-btn
                  v-else
                  color="success"
                  :disabled="!valid"
                  type="submit"
                  :loading="loading"
                  >Add</v-btn
                >
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </template>
      <span v-if="edit">Edit location</span>
      <span v-else>Add new location</span>
    </v-tooltip>
  </div>
</template>

<script>
import { locationsCollection } from '@/firebaseConfig';
import UploadImage from '@/components/UploadImage';
import AddressLookup from '@/components/AddressLookup';
import FEATURES_LIST from '@/constants/features';

const blankLocation = {
  name: '',
  description: '',
  image: '',
  image_download_url: '',
  address: {},
  features: []
};

export default {
  name: 'AddLocation',
  components: {
    UploadImage,
    AddressLookup
  },
  props: {
    edit: Boolean
  },
  data() {
    return {
      location: {
        name: '',
        description: '',
        image: '',
        image_download_url: '',
        address: {},
        features: []
      },
      dialog: false,
      valid: true,
      loading: false
    };
  },
  computed: {
    featuresList() {
      return FEATURES_LIST;
    },
    currentLocation: {
      get() {
        return this.$store.state.currentLocation;
      },
      set(value) {
        this.$store.commit('setCurrentLocation', value);
      }
    }
  },
  mounted() {
    this.setCurrentLocationImage();
  },
  methods: {
    initLocation() {
      if (this.edit) {
        this.location = { ...this.currentLocation };
      } else {
        this.location = blankLocation;
        this.$refs.form.reset();
      }
    },
    setDownloadUrl(event) {
      if (event) {
        this.location.image = event.path;
        this.location.image_download_url = event.url;
      } else {
        this.location.image = '';
        this.location.image_download_url = null;
      }
    },
    cancel() {
      this.dialog = false;
      this.location = blankLocation;
    },
    setCurrentLocationImage() {
      if (this.location.image_download_url) {
        this.currentLocationImage = this.location.image_download_url;
      }
    },
    createLocation() {
      if (this.$refs.form.validate()) {
        let loc = JSON.parse(JSON.stringify(this.location));
        locationsCollection
          .add(loc)
          .then(() => {
            this.dialog = false;
            this.$store.dispatch('createSnackbar', {
              color: 'success',
              text: `New location created successfully: ${this.location.name}`
            });
            this.location = blankLocation;
            this.setCurrentLocationImage();
          })
          .catch(error => {
            this.$store.dispatch('createSnackbar', {
              color: 'error',
              text: `Error adding location: ${error}`
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    editLocation() {
      if (this.$refs.form.validate()) {
        if (!this.location.image) {
          this.location.image = this.currentLocation.image;
        }
        let loc = JSON.parse(JSON.stringify(this.location));
        locationsCollection
          .doc(this.currentLocation.id)
          .set(loc)
          .then(() => {
            this.dialog = false;
            this.$store.dispatch('createSnackbar', {
              color: 'success',
              text: `Location updated successfully: ${this.location.name}`
            });
            this.currentLocation = Object.assign(
              this.currentLocation,
              this.location
            );
            this.setCurrentLocationImage();
            this.location = blankLocation;
          })
          .catch(() => {
            this.$store.dispatch('createSnackbar', {
              color: 'error',
              text: `Error updating location: ${this.location.name}`
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    }
  }
};
</script>
